import { createEffect, createMemo, createSignal, onCleanup, onMount, Show } from "solid-js";
import { Outlet, useLocation, useNavigate, useRouterState } from "@tanstack/solid-router";
import type { AbstractPowerSyncDatabase } from "@powersync/common";
import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";

import { appState, Skeleton } from "@fleet/ui";
import { useTheme } from "@/providers/theme-provider";
import { useI18n } from "@/providers/i18n-provider";
import { useSearchPalette } from "@/features/search/hooks/use-search-palette";

// Layout specific components and providers
import { AppBackground } from "@/components/app-background";
import { AppHeader } from "@/components/app-header";
import { OfflineBanner } from "@/components/offline-banner";
import { ListThemeProvider } from "@/providers/list-theme-provider";
import { CommandPalette } from "@/features/search/components/command-palette";

// Layout module parts
import { useInteractionFlag } from "@fleet/ui";
import { AppLayoutContext } from "../context";
import { SwipeIndicators } from "./swipe-indicators";
import { AppLayoutSidebar } from "./app-layout-sidebar";
import { AppLayoutDialogs } from "./app-layout-dialogs";
import { usePageSwipeGestures } from "../hooks/use-page-swipe-gestures";
import { useAppDialogs } from "../hooks/use-app-dialogs";
import { useAppThemeState } from "../hooks/use-app-theme-state";
import { useAppShortcuts } from "../hooks/use-app-shortcuts";
import { getListNameForPath, getOrderedListPaths } from "../helpers";

interface AppLayoutContentProps {
  // Null while PowerSync's local DB is still initializing. The static shell
  // renders regardless; only data-bound regions show a skeleton until it's ready.
  db: AbstractPowerSyncDatabase | null;
  activeListId: string | null;
  activeDragId: () => string | null;
  handleDragStart: (e: DragEvent) => void;
  handleDragEnd: (e: DragEvent) => void;
}

/**
 * Granular skeleton for the main content region (the routed `<Outlet />`),
 * shown while PowerSync's local DB is still initializing. Mirrors the rough
 * structure of a workspace screen (header strip + content rows) so the layout
 * doesn't jump when the real content mounts.
 */
function ContentSkeleton() {
  return (
    <div class="flex flex-1 flex-col min-h-0 h-full gap-4 p-6">
      <div class="flex items-center justify-between">
        <Skeleton class="h-7 w-48" />
        <Skeleton class="h-9 w-28" />
      </div>
      <div class="flex flex-col gap-3">
        <Skeleton class="h-16 w-full" />
        <Skeleton class="h-16 w-full" />
        <Skeleton class="h-16 w-full" />
        <Skeleton class="h-16 w-full" />
      </div>
    </div>
  );
}

function getAdjacentListNamesHelper(
  paths: string[],
  currentIndex: number,
  t: (message: MessageDescriptor) => string,
) {
  if (paths.length === 0 || currentIndex === -1) {
    return { prev: "", next: "" };
  }
  const nextIndex = (currentIndex + 1) % paths.length;
  const prevIndex = (currentIndex - 1 + paths.length) % paths.length;

  return {
    prev: getListNameForPath(paths[prevIndex] ?? "", t),
    next: getListNameForPath(paths[nextIndex] ?? "", t),
  };
}

function getNextPath(isNext: boolean, paths: string[], currentIndex: number): string | undefined {
  if (paths.length === 0 || currentIndex === -1) {
    return undefined;
  }
  const nextIndex = isNext
    ? (currentIndex + 1) % paths.length
    : (currentIndex - 1 + paths.length) % paths.length;
  return paths[nextIndex];
}

function getCurrentPathIndex(path: string, orderedListPaths: string[]): number {
  if (path.includes("/todo/")) {
    return -1;
  }
  return orderedListPaths.indexOf(path);
}

function useGlobalEventListeners(
  isSidebarOpen: () => boolean,
  setIsSidebarOpen: (open: boolean) => void,
) {
  onMount(() => {
    const handleEscapeForSidebar = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isSidebarOpen()) {
        setIsSidebarOpen(false);
      }
    };

    globalThis.addEventListener("keydown", handleEscapeForSidebar);

    onCleanup(() => {
      globalThis.removeEventListener("keydown", handleEscapeForSidebar);
    });
  });
}

function isSolidDndRegistryNoise(args: unknown[]): boolean {
  const [firstArg] = args;
  return (
    typeof firstArg === "string" &&
    firstArg.includes("nonexistent") &&
    (firstArg.includes("draggable") ||
      firstArg.includes("droppable") ||
      firstArg.includes("sensor") ||
      firstArg.includes("transformer"))
  );
}

const stats = () => undefined;

export function AppLayoutContent(props: AppLayoutContentProps) {
  onMount(() => {
    appState.markAsReady();

    const originalWarn = console.warn;
    const originalError = console.error;

    console.warn = (...args: unknown[]) => {
      if (isSolidDndRegistryNoise(args)) {
        return;
      }
      originalWarn(...args);
    };

    console.error = (...args: unknown[]) => {
      if (isSolidDndRegistryNoise(args)) {
        return;
      }
      originalError(...args);
    };

    onCleanup(() => {
      console.warn = originalWarn;
      console.error = originalError;
    });
  });

  const navigate = useNavigate();
  const searchPalette = useSearchPalette();
  const { t, tDynamic } = useI18n();

  const handleCloseSearch = () => {
    searchPalette.close();
  };

  const location = useLocation();
  const routerState = useRouterState();

  const [committedPath, setCommittedPath] = createSignal(location().pathname);

  const orderedListPaths = createMemo(() => getOrderedListPaths());

  const currentPathIndex = () => getCurrentPathIndex(location().pathname, orderedListPaths());

  const getAdjacentListNames = () =>
    getAdjacentListNamesHelper(orderedListPaths(), currentPathIndex(), t);

  const navigateList = (isNext: boolean) => {
    if (routerState().status === "pending") {
      return;
    }
    const targetPath = getNextPath(isNext, orderedListPaths(), currentPathIndex());
    if (targetPath) {
      void navigate({ to: targetPath });
    }
  };

  const {
    handlers: pageSwipeHandlers,
    swipeListTx,
    isSwipingList,
    swipeListTy,
    isSwipingListY,
    swipeListAnimating,
    swipeListYAnimating,
    LIST_SWIPE_INDICATOR_TRIGGER_PX,
    LIST_SWIPE_COMMIT_THRESHOLD_PX,
  } = usePageSwipeGestures({
    location,
    navigate,
    orderedListPaths,
    currentPathIndex,
    navigateList,
  });

  createEffect(() => {
    const path = location().pathname;
    setCommittedPath(path);
  });

  const isNavigating = () => routerState().status === "pending";

  const theme = useTheme();

  const {
    isSidebarOpen,
    setIsSidebarOpen,
    shortcutsOpen,
    setShortcutsOpen,
    openNewListDialog,
    openNewGroupDialog,
  } = useAppDialogs(location);

  useAppShortcuts({
    searchPalette,
    location,
    navigate,
    theme,
    tDynamic,
    listDialogOpen: () => false,
    choiceDialogOpen: () => false,
    groupDialogOpen: () => false,
    shareDialogOpen: () => false,
    shortcutsOpen,
    setShortcutsOpen,
    setIsSidebarOpen,
    openNewListDialog,
    openNewGroupDialog,
    navigateList,
  });

  useGlobalEventListeners(isSidebarOpen, setIsSidebarOpen);

  // Declares who owns horizontal swiping while the drawer is open: the
  // page-level swipe gesture is bound to the window and must stand down (see
  // `use-page-swipe-gestures.ts`'s `SUPPRESS_FLAGS`).
  useInteractionFlag({ "nav-drawer.open": isSidebarOpen });

  const { listContextId, activeBlur, activeListTokens, activeListBackground, glows } =
    useAppThemeState({
      committedPath: () => location().pathname,
      theme,
    });

  return (
    <ListThemeProvider listId={listContextId()}>
      <AppBackground
        class="flex h-dvh text-foreground overflow-hidden"
        isBlur={activeBlur()}
        background={activeListBackground()}
        patternType={activeListTokens()?.patternType ?? theme.tokens()?.patternType}
        patternOpacity={activeListTokens()?.patternOpacity ?? theme.tokens()?.patternOpacity}
        {...glows()}
      >
        {/* Skip-to-content: first focusable element; visually hidden until
            focused, then anchors keyboard/AT users straight to <main>. */}
        <a
          href="#main-content"
          class="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-lg focus:bg-primary focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-on-primary focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-ring"
        >
          {t(msg`Skip to content`)}
        </a>

        <SwipeIndicators
          isSwipingList={isSwipingList}
          swipeListTx={swipeListTx}
          isSwipingListY={isSwipingListY}
          swipeListTy={swipeListTy}
          LIST_SWIPE_INDICATOR_TRIGGER_PX={LIST_SWIPE_INDICATOR_TRIGGER_PX}
          LIST_SWIPE_COMMIT_THRESHOLD_PX={LIST_SWIPE_COMMIT_THRESHOLD_PX}
          location={location}
          getAdjacentListNames={getAdjacentListNames}
        />

        <AppLayoutSidebar isOpen={isSidebarOpen} setOpen={setIsSidebarOpen} stats={stats} />

        {/* ── Main content ───────────────────────────────────────── */}
        <main
          id="main-content"
          tabindex="-1"
          class="flex flex-1 flex-col overflow-hidden bg-transparent pt-[var(--safe-area-inset-top)] pr-[var(--safe-area-inset-right)] pb-[var(--safe-area-inset-bottom)] outline-none"
          {...pageSwipeHandlers}
        >
          <AppHeader onMenuClick={() => setIsSidebarOpen(true)} isNavigating={isNavigating} />
          <OfflineBanner />
          <div
            class="flex flex-1 flex-col min-h-0 overflow-hidden pr-[var(--safe-area-inset-right)] pl-[var(--safe-area-inset-left)]"
            style={{
              transform: `translate3d(${swipeListTx()}px, ${swipeListTy()}px, 0)`,
              transition:
                swipeListAnimating() || swipeListYAnimating()
                  ? "transform 240ms cubic-bezier(0.32, 0.72, 0, 1)"
                  : "none",
              "will-change":
                isSwipingList() || isSwipingListY() || swipeListAnimating() || swipeListYAnimating()
                  ? "transform"
                  : "auto",
            }}
          >
            <AppLayoutContext.Provider
              value={{
                activeDragId: () => props.activeDragId(),
                committedPath: () => committedPath(),
              }}
            >
              <div class="flex flex-1 flex-col min-h-0 h-full">
                {/* Data-bound region: child routes query PowerSync directly, so
                    show a granular content skeleton until the local DB is ready. */}
                <Show when={props.db} fallback={<ContentSkeleton />}>
                  {/*
                    Canonical page-entrance: animate the routed view in ONCE per
                    navigation. Keyed by `pathname` only (search params excluded)
                    so filtering/searching within a tab never re-animates the page.
                    This wrapper is deliberately SEPARATE from the parent div that
                    owns the `translate3d` swipe offset, so the slide-in transform
                    never fights the swipe-carousel transform. GPU-only props
                    (transform + opacity) via `slide-in-up`; respects
                    reduced-motion through the global guard.
                  */}
                  <Show keyed when={location().pathname}>
                    <div class="flex flex-1 flex-col min-h-0 h-full motion-safe:animate-slide-in-up will-change-[transform,opacity]">
                      <Outlet />
                    </div>
                  </Show>
                </Show>
              </div>
            </AppLayoutContext.Provider>
          </div>
        </main>

        <AppLayoutDialogs shortcutsOpen={shortcutsOpen} setShortcutsOpen={setShortcutsOpen} />
      </AppBackground>

      {/* Command Palette — rendered OUTSIDE AppBackground to avoid blur compositor flash */}
      <CommandPalette isOpen={searchPalette.isOpen()} onClose={handleCloseSearch} />
    </ListThemeProvider>
  );
}
